import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Reveal, SectionLabel, MaskLines } from "../components/site/Reveal";
import { PipelineStrip } from "../components/site/PipelineStrip";
import { useSeo } from "../components/site/Seo";
import { useContent } from "../lib/content";
import { mediaUrl } from "../lib/api";

const RoleCard = ({ title, points, heat }) => (
  <Reveal className={`rounded-3xl p-7 sm:p-8 ${heat ? "glass" : "border hairline bg-white/[0.02]"}`}>
    <p className={`font-mono text-[11px] uppercase tracking-[0.2em] ${heat ? "text-[#ff8a4c]" : "text-[#7fd6fb]"}`}>{title}</p>
    <ul className="mt-6 space-y-3.5">
      {(points || []).map((p) => (
        <li key={p} className="flex items-start gap-3 text-white"><Check className={`w-4 h-4 mt-1 shrink-0 ${heat ? "text-[#ff5500]" : "text-[#4fc3f7]"}`} />{p}</li>
      ))}
    </ul>
  </Reveal>
);

export default function Programma() {
  const p = useContent().program;
  useSeo("Il programma", p.hero_text);
  const phases = Array.isArray(p.phases) ? p.phases : [];
  return (
    <main data-testid="programma-page" className="pt-28 sm:pt-36">
      <section className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <SectionLabel tone="heat">{p.hero_label}</SectionLabel>
        <h1 className="mt-7 max-w-5xl font-display font-extrabold text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.98]">
          <MaskLines onLoad text={p.hero_title} />
        </h1>
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.8 }}
          className="mt-8 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed">{p.hero_text}</motion.p>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 1 }}
          className="mt-14 relative clip-notch rounded-3xl overflow-hidden aspect-[16/10] sm:aspect-[21/9]">
          <img src={mediaUrl(p.image)} alt="Impianto fotovoltaico su abitazione" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#060a14] via-[#060a14]/40 to-transparent" />
          <div className="absolute bottom-4 inset-x-4 sm:bottom-6 sm:inset-x-6 hidden sm:block"><PipelineStrip /></div>
        </motion.div>
        <div className="sm:hidden mt-4"><PipelineStrip /></div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
        <h2 className="font-display font-extrabold text-white text-3xl sm:text-5xl"><MaskLines text={p.phases_title} /></h2>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {phases.map((ph, i) => (
            <Reveal key={i} delay={i * 0.07} className="rounded-2xl border hairline bg-white/[0.02] p-5 flex flex-col" data-testid={`program-phase-${i}`}>
              <span className="font-display italic font-black text-5xl text-outline">0{i + 1}</span>
              <p className="mt-4 font-display-n font-bold text-white text-lg">{ph.title}</p>
              <p className="mt-2 text-sm text-slate-300 leading-relaxed flex-1">{ph.text}</p>
              <p className={`mt-5 font-mono text-[10px] uppercase tracking-[0.18em] ${ph.owner === "TERMONLINE" ? "text-[#ff8a4c]" : "text-[#7fd6fb]"}`}>{ph.owner}</p>
            </Reveal>
          ))}
        </div>
        <div className="mt-20 grid md:grid-cols-2 gap-5">
          <RoleCard title={p.us_title} points={p.us_points} heat />
          <RoleCard title={p.you_title} points={p.you_points} />
        </div>
      </section>

      <section className="border-t hairline bg-[#070c18]">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 py-24">
          <h2 className="font-display font-extrabold text-white text-3xl sm:text-4xl">{p.faq_title}</h2>
          <Accordion type="single" collapsible className="mt-10" data-testid="program-faq">
            {(p.faq || []).map((f, i) => (
              <AccordionItem key={i} value={`f${i}`} className="border-b hairline">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-white text-base sm:text-lg hover:no-underline py-5">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-300 text-[15px] leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <Link to="/#consulenza" data-testid="program-cta"
            className="group mt-14 inline-flex items-center gap-2 h-14 px-7 rounded-full bg-[#ff5500] hover:bg-[#ff6a1f] text-white font-semibold transition-colors">
            Richiedi una consulenza<ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </main>
  );
}
